import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { api, getErrorMessage, downloadFile } from "@/lib/seoApi";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Zap, LogIn, KeyRound, Download, Upload, Send, CheckCircle2, XCircle, AlertTriangle, Copy,
} from "lucide-react";
import { toast } from "sonner";

const T = {
  en: {
    title: "IndexNow Submitter",
    sub: "Instantly tell Bing, Yandex, Naver, Seznam and Yep that your pages changed - one ping reaches them all. Because ChatGPT and Copilot read Bing's index, this is the fastest way into AI answers.",
    gateTitle: "Sign in to use IndexNow",
    gateBody: "A free account lets you generate your key and push URLs to every IndexNow search engine at once.",
    gateCta: "Sign in or create a free account",
    step1: "1. Generate your key",
    step1Body: "IndexNow uses a secret key to confirm you own the domain. Generate one, then download it as a file.",
    genKey: "Generate a key",
    genKeyLoading: "Generating...",
    yourKey: "Your key",
    copyKey: "Copy key",
    downloadKey: "Download key file",
    step2: "2. Upload the key file to your site root",
    step2Body: "Place the downloaded file so it loads at this exact address (the engines fetch it to verify ownership):",
    step2Note: "On cPanel/FTP: drop the .txt file directly in public_html (or your site root).",
    step3: "3. Submit your URLs",
    host: "Your domain",
    hostPh: "example.com",
    urls: "URLs to submit (one per line)",
    urlsPh: "https://example.com/\nhttps://example.com/new-page\nhttps://example.com/updated-article",
    keyLoc: "Key file URL (optional - only if not at the root)",
    submit: "Submit to IndexNow",
    submitting: "Submitting...",
    needKey: "Generate a key first (step 1).",
    okTitle: "Submitted",
    failTitle: "Submission not accepted",
    submittedCount: (n) => `${n} URL(s) sent to Bing, Yandex, Naver, Seznam and Yep.`,
    whatNext: "IndexNow confirms receipt instantly. Each engine then decides when to crawl based on its own quality checks - submission speeds up discovery, it does not force indexing.",
    reminder: "Reminder: Google does not use IndexNow. For Google, submit your sitemap in Search Console.",
  },
  fr: {
    title: "Soumission IndexNow",
    sub: "Dites instantanément à Bing, Yandex, Naver, Seznam et Yep que vos pages ont changé - un seul ping les atteint tous. Comme ChatGPT et Copilot lisent l'index de Bing, c'est la voie la plus rapide vers les réponses IA.",
    gateTitle: "Connectez-vous pour utiliser IndexNow",
    gateBody: "Un compte gratuit vous permet de générer votre clé et de pousser vos URL vers tous les moteurs IndexNow d'un coup.",
    gateCta: "Connexion ou création de compte gratuit",
    step1: "1. Générez votre clé",
    step1Body: "IndexNow utilise une clé secrète pour confirmer que vous possédez le domaine. Générez-en une, puis téléchargez-la en fichier.",
    genKey: "Générer une clé",
    genKeyLoading: "Génération...",
    yourKey: "Votre clé",
    copyKey: "Copier la clé",
    downloadKey: "Télécharger le fichier de clé",
    step2: "2. Téléversez le fichier de clé à la racine de votre site",
    step2Body: "Placez le fichier téléchargé pour qu'il soit accessible à cette adresse exacte (les moteurs le lisent pour vérifier la propriété) :",
    step2Note: "Sur cPanel/FTP : déposez le fichier .txt directement dans public_html (ou la racine de votre site).",
    step3: "3. Soumettez vos URL",
    host: "Votre domaine",
    hostPh: "exemple.com",
    urls: "URL à soumettre (une par ligne)",
    urlsPh: "https://exemple.com/\nhttps://exemple.com/nouvelle-page\nhttps://exemple.com/article-mis-a-jour",
    keyLoc: "URL du fichier de clé (optionnel - seulement si pas à la racine)",
    submit: "Soumettre à IndexNow",
    submitting: "Soumission...",
    needKey: "Générez d'abord une clé (étape 1).",
    okTitle: "Soumis",
    failTitle: "Soumission non acceptée",
    submittedCount: (n) => `${n} URL envoyée(s) à Bing, Yandex, Naver, Seznam et Yep.`,
    whatNext: "IndexNow confirme la réception instantanément. Chaque moteur décide ensuite quand explorer selon ses propres critères de qualité - la soumission accélère la découverte, elle ne force pas l'indexation.",
    reminder: "Rappel : Google n'utilise pas IndexNow. Pour Google, soumettez votre sitemap dans Search Console.",
  },
};

export default function IndexNow() {
  const { user, ready } = useAuth();
  const { lang } = useLang();
  const t = T[lang];
  const location = useLocation();

  const [key, setKey] = useState("");
  const [keyLoading, setKeyLoading] = useState(false);
  const [host, setHost] = useState("");
  const [urlsText, setUrlsText] = useState("");
  const [keyLocation, setKeyLocation] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const cleanHost = host.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/+$/, "");

  const genKey = async () => {
    setKeyLoading(true);
    try {
      const { data } = await api.get("/indexnow/key");
      setKey(data.key);
      toast.success(lang === "fr" ? "Clé générée" : "Key generated");
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setKeyLoading(false);
    }
  };

  const copyKey = async () => {
    await navigator.clipboard.writeText(key);
    toast.success(lang === "fr" ? "Clé copiée" : "Key copied");
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!key) {
      setError(t.needKey);
      return;
    }
    const urls = urlsText.split("\n").map((u) => u.trim()).filter(Boolean);
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const { data } = await api.post("/indexnow/submit", {
        host: cleanHost,
        key,
        urls,
        key_location: keyLocation.trim() || null,
      });
      setResult(data);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-10 py-8 sm:py-10">
      <div>
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">{t.title}</h1>
        <p className="mt-2 text-muted-foreground">{t.sub}</p>
      </div>

      {ready && !user ? (
        <div className="mt-8 rounded-lg border p-8 text-center bg-card" data-testid="indexnow-signin-gate">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground">
            <LogIn className="h-6 w-6" />
          </div>
          <h2 className="mt-4 text-lg font-heading font-semibold">{t.gateTitle}</h2>
          <p className="mt-2 text-sm text-muted-foreground">{t.gateBody}</p>
          <Button asChild className="mt-5" size="lg" data-testid="indexnow-signin-button">
            <Link to={`/login?next=${encodeURIComponent(location.pathname)}`}>{t.gateCta}</Link>
          </Button>
        </div>
      ) : (
        <div className="mt-8 space-y-6">
          {/* Step 1: key */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-heading flex items-center gap-2">
                <KeyRound className="h-4 w-4" /> {t.step1}
              </CardTitle>
              <p className="text-sm text-muted-foreground">{t.step1Body}</p>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button onClick={genKey} disabled={keyLoading} variant={key ? "outline" : "default"} data-testid="indexnow-genkey-button">
                <KeyRound className="h-4 w-4 mr-2" />
                {keyLoading ? t.genKeyLoading : t.genKey}
              </Button>
              {key && (
                <div className="rounded-md border bg-muted/40 p-3 space-y-3" data-testid="indexnow-key-block">
                  <div>
                    <Label className="text-xs">{t.yourKey}</Label>
                    <p className="font-mono text-sm break-all mt-1">{key}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" onClick={copyKey} data-testid="indexnow-copykey-button">
                      <Copy className="h-3.5 w-3.5 mr-1.5" /> {t.copyKey}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => downloadFile(key, `${key}.txt`)} data-testid="indexnow-downloadkey-button">
                      <Download className="h-3.5 w-3.5 mr-1.5" /> {t.downloadKey}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Step 2: upload */}
          {key && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-heading flex items-center gap-2">
                  <Upload className="h-4 w-4" /> {t.step2}
                </CardTitle>
                <p className="text-sm text-muted-foreground">{t.step2Body}</p>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border code-scroll p-3 font-mono text-xs break-all">
                  https://{cleanHost || t.hostPh}/{key}.txt
                </div>
                <p className="mt-2 text-xs text-muted-foreground">{t.step2Note}</p>
              </CardContent>
            </Card>
          )}

          {/* Step 3: submit */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-heading flex items-center gap-2">
                <Send className="h-4 w-4" /> {t.step3}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={submit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="in-host">{t.host}</Label>
                  <Input id="in-host" value={host} onChange={(e) => setHost(e.target.value)} placeholder={t.hostPh} data-testid="indexnow-host-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="in-urls">{t.urls}</Label>
                  <textarea
                    id="in-urls"
                    value={urlsText}
                    onChange={(e) => setUrlsText(e.target.value)}
                    placeholder={t.urlsPh}
                    rows={5}
                    className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    data-testid="indexnow-urls-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="in-keyloc">{t.keyLoc}</Label>
                  <Input id="in-keyloc" value={keyLocation} onChange={(e) => setKeyLocation(e.target.value)} placeholder={`https://${cleanHost || "exemple.com"}/${key || "key"}.txt`} data-testid="indexnow-keyloc-input" />
                </div>
                <Button type="submit" size="lg" disabled={loading} className="h-11" data-testid="indexnow-submit-button">
                  <Zap className="h-4 w-4 mr-2" />
                  {loading ? t.submitting : t.submit}
                </Button>
              </form>
            </CardContent>
          </Card>

          {error && (
            <Alert variant="destructive" data-testid="indexnow-error">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>{t.failTitle}</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {result && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
              <Alert variant={result.ok ? "default" : "destructive"} className={result.ok ? "border-emerald-200 bg-emerald-50" : ""} data-testid="indexnow-result">
                {result.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4" />}
                <AlertTitle>{result.ok ? t.okTitle : t.failTitle}</AlertTitle>
                <AlertDescription>
                  {result.ok && <p className="font-medium">{t.submittedCount(result.submitted)}</p>}
                  <p className="mt-1">{result.message}</p>
                  {result.ok && <p className="mt-2 text-xs">{t.whatNext}</p>}
                </AlertDescription>
              </Alert>
            </motion.div>
          )}

          <div className="flex items-start gap-2 text-xs text-muted-foreground">
            <Badge variant="outline" className="shrink-0">Google</Badge>
            <span>{t.reminder}</span>
          </div>
        </div>
      )}
    </div>
  );
}
